import React, { useState, useEffect } from "react";
import { MessageSquarePlus } from "lucide-react";
import ImagePasteArea from "./ModelPlanner/ImagePasteArea";
import { Modal, ModalBody, ModalFooter, Dropdown, Button } from './ui';

const NewFeedbackModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    request: "",
    details: "",
    type: "",
    priority: "Medium"
  });
  const [screenshots, setScreenshots] = useState([]);
  const [tempFeedbackId] = useState(() => crypto.randomUUID());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const typeOptions = [
    { value: 'Bug', label: 'Bug' },
    { value: 'New Feature', label: 'New Feature' },
    { value: 'UI Issue', label: 'UI Issue' },
  ];

  const priorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
  ];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        request: "",
        details: "",
        type: "",
        priority: "Medium"
      });
      setScreenshots([]);
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.request.trim()) {
      newErrors.request = "Request is required";
    }
    if (!formData.type) {
      newErrors.type = "Type is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const feedbackData = {
        ...formData,
        screenshot_urls: screenshots.length > 0 ? screenshots : []
      };
      await onSave(feedbackData);
      handleClose();
    } catch (error) {
      console.error("Error saving feedback:", error);
      setErrors({ submit: error.message || "Failed to save feedback. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      request: "",
      details: "",
      type: "",
      priority: "Medium"
    });
    setScreenshots([]);
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <Modal trackAs="NewFeedbackModal" isOpen={isOpen} onClose={handleClose} size="lg">
      {/* Custom Header with Icon and Title */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-orange)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MessageSquarePlus size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
              }}
            >
              New Feedback Request
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              Submit a bug report, feature request, or UI issue
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Error message */}
          {errors.submit && (
            <div
              style={{
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--danger)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Two column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left Column: Request Details */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Request Details
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Request */}
                <div>
                  <label
                    htmlFor="request"
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Request <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <textarea
                    id="request"
                    name="request"
                    value={formData.request}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe your request or issue..."
                    autoFocus
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: errors.request ? '1px solid var(--danger)' : '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color var(--transition-fast)',
                    }}
                    onFocus={(e) => {
                      if (!errors.request) {
                        e.target.style.borderColor = 'var(--cta-blue)';
                      }
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      if (!errors.request) {
                        e.target.style.borderColor = 'var(--gray-600)';
                      }
                    }}
                  />
                  {errors.request && (
                    <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>
                      {errors.request}
                    </div>
                  )}
                </div>

                {/* Details */}
                <div>
                  <label
                    htmlFor="details"
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Details / Loom Link
                  </label>
                  <textarea
                    id="details"
                    name="details"
                    value={formData.details}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Additional details or paste a Loom link..."
                    style={{
                      width: '100%',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      transition: 'border-color var(--transition-fast)',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--cta-blue)';
                      e.target.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'var(--gray-600)';
                    }}
                  />
                </div>

                {/* Screenshots */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      color: 'var(--gray-300)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    Screenshots (Optional)
                  </label>
                  <ImagePasteArea
                    images={screenshots}
                    onImagesChange={setScreenshots}
                    projectId={tempFeedbackId}
                    clientId="feedback"
                    disabled={loading}
                    type="feedback"
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Type & Priority */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Classification
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Type */}
                <Dropdown
                  label="Type"
                  options={typeOptions}
                  value={formData.type}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, type: value }));
                    if (errors.type) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.type;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Select type"
                  error={errors.type}
                  required
                />

                {/* Priority */}
                <Dropdown
                  label="Priority"
                  options={priorityOptions}
                  value={formData.priority}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, priority: value }));
                  }}
                  placeholder="Select priority"
                />
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-3)',
              width: '100%',
            }}
          >
            <Button variant="ghost" onClick={handleClose} type="button">
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={loading} disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default NewFeedbackModal;